<script setup>
/**
 * Employee self-service Profile page. Mirrors Vue2's Feeds/ChangeImage.vue:
 * opened via /change_image?id=<docId> (not an admin-authenticated route —
 * see meta.public in additional-routes.js), shows the employee's profile
 * photo + display name, and lets them replace the photo (auto-cropped to a
 * 300x300 square client-side, since the Vue2 vue-image-crop-upload package
 * has no Vue 3 equivalent here).
 */
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { fetchUser } from "@/features/users/api";
import { changeProfileImage } from "./api";

const route = useRoute();
const docId = route.query.id || "";

const loading = ref(true);
const saving = ref(false);
const error = ref("");
const displayName = ref("");
const imageUrl = ref("");
const fileInput = ref(null);

onMounted(async () => {
  if (!docId) {
    error.value = "Missing profile id.";
    loading.value = false;
    return;
  }
  try {
    const u = await fetchUser(docId);
    displayName.value = u?.display_name ?? "";
    imageUrl.value = u?.image ?? "";
  } catch {
    error.value = "Couldn't load profile.";
  } finally {
    loading.value = false;
  }
});

function pickFile() {
  fileInput.value?.click();
}

function onFileSelected(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => {
    const img = new Image();
    img.onload = () => uploadCroppedImage(img);
    img.src = ev.target.result;
  };
  reader.readAsDataURL(file);
}

async function uploadCroppedImage(img) {
  const size = 300;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const scale = Math.max(size / img.width, size / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.9);

  saving.value = true;
  error.value = "";
  try {
    const res = await changeProfileImage({
      filename: `${docId}.jpg`,
      image: dataUrl.split(",")[1],
      currentImage: imageUrl.value,
    });
    if (res.status) {
      imageUrl.value = res.data.url;
    } else {
      error.value = res.message || "Couldn't update profile image.";
    }
  } catch {
    error.value = "Couldn't update profile image.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <section class="d-flex flex-column align-center pa-6" style="max-width: 420px; margin: 0 auto">
    <VToolbar density="compact" flat color="transparent" class="mb-4 w-100">
      <VToolbarTitle class="text-h6 font-weight-bold">Profile</VToolbarTitle>
    </VToolbar>

    <VAlert v-if="error" type="error" class="mb-4 w-100">{{ error }}</VAlert>

    <VProgressCircular v-if="loading" indeterminate class="mt-8" />

    <template v-else-if="!error || imageUrl || displayName">
      <div class="position-relative mt-4">
        <VAvatar size="200" class="elevation-3">
          <VImg v-if="imageUrl" :src="imageUrl" cover />
          <VIcon v-else icon="ri-user-line" size="96" />
        </VAvatar>
        <VBtn
          icon="ri-pencil-line"
          size="small"
          color="primary"
          class="position-absolute"
          style="bottom: 4px; right: 4px"
          :loading="saving"
          @click="pickFile"
        />
      </div>
      <input ref="fileInput" type="file" accept="image/*" class="d-none" @change="onFileSelected" />
      <p class="text-h6 font-weight-bold mt-4 text-center">{{ displayName }}</p>
    </template>
  </section>
</template>
